import { useRecoilValue } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    fetchCreatePatient,
    fetchGetPatient,
    fetchSaveSliceForImageForPatient,
    fetchSyncPatient,
    SaveSliceForImageForPatientRequestBody,
} from '~/backend/connector-api/connector-components';
import { SeriesSyncPayload } from '~/backend/connector-api/connector-schemas';
import { Image3D } from '~/domain/patient/image3d';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import AppConfig from '~/utils/appconfig';
import { getErrorMessage, handleError } from '~/utils/errors';

type EnsureCachedInput = {
    patientId: string;
    seriesInstanceUID: string;
    studyInstanceUID: string;
};

// The nginx proxy caps request bodies at 100 MB. A typical CT slice is
// <1 MB but T1 MR, 4D CT, and thin-cut diagnostic series can push the
// average much higher, so keep the per-request batch conservative.
const UPLOAD_BATCH_SIZE = 20;

/**
 * Uploads an image series from the local patient repository to the
 * connector when the connector does not already have it cached. Meant
 * to run before any AI-assistant task submission so that the backend
 * can actually read the input the user is targeting.
 *
 * No-op in non-connector mode. In connector mode it queries the
 * connector for the patient; if the target series is already known it
 * returns immediately, otherwise it creates the patient (idempotent),
 * syncs the series metadata to get back an image ID, and POSTs the
 * slice blobs in batches.
 *
 * On failure the hook rethrows so the caller's try/catch aborts the
 * job submission — a half-uploaded image must not be processed.
 */
export default function useEnsureImageCached() {
    const patientRepository = useRecoilValue(patientRepositoryState);

    const ensureCached = async ({
        patientId,
        seriesInstanceUID,
        studyInstanceUID,
    }: EnsureCachedInput): Promise<void> => {
        if (!AppConfig.isConnectorMode) {
            return;
        }

        try {
            const existingImageId = await findImageIdOnConnector(
                patientId,
                seriesInstanceUID
            );
            if (existingImageId) {
                return;
            }

            const patient = patientRepository.getByPatientId(patientId);
            if (!patient) {
                throw new Error(
                    `Patient ${patientId} not found in local repository.`
                );
            }

            const image = patient.images.find(
                (img: Image3D) => img.seriesUid === seriesInstanceUID
            );
            if (!image) {
                throw new Error(
                    `Image series ${seriesInstanceUID} not found for ` +
                        `patient ${patientId} in local repository.`
                );
            }

            // Step 1: ensure the patient record exists on the connector.
            // `fetchCreatePatient` is idempotent from the caller's point
            // of view — if the patient already exists the server
            // responds with an error, which we swallow and move on.
            try {
                await fetchCreatePatient({
                    body: {
                        patientID: patientId,
                        patientName: patient.dataSet.PatientName,
                    },
                });
            } catch {
                // Patient may already exist on the connector; continue.
            }

            // Step 2: sync the series metadata so the connector
            // allocates an Image3D record we can attach slices to.
            const slices = image.imageSlices;
            const modality = image.firstSlice.modality;
            const syncPayload: SeriesSyncPayload[] = [
                {
                    dicomSystemName: 'web',
                    modality,
                    studyInstanceUID,
                    seriesInstanceUID,
                    datasets: slices.map((slice) => ({
                        modality: slice.modality,
                        studyInstanceUID,
                        seriesInstanceUID,
                        sopInstanceUID: slice.sopInstanceUid,
                    })),
                },
            ];
            const syncResult = await fetchSyncPatient({
                pathParams: { patientID: patientId },
                body: syncPayload,
            });

            const createdImageId = syncResult?.images?.find(
                (img) => img.seriesInstanceUID === seriesInstanceUID
            )?.id;
            if (!createdImageId) {
                throw new Error(
                    `Connector did not return an image ID for series ` +
                        `${seriesInstanceUID} after sync.`
                );
            }

            // Step 3: upload slice blobs in batches to stay under the
            // nginx client_max_body_size cap.
            const sliceBlobs = Array.from(image.getSlicesAsDicomBlobs());
            const sliceCount = sliceBlobs.length;
            const batchCount = Math.ceil(sliceCount / UPLOAD_BATCH_SIZE);

            enqueueSnackbar(
                `Uploading ${sliceCount} image slices to connector…`,
                { variant: 'info' }
            );

            for (let batch = 0; batch < batchCount; batch++) {
                const start = batch * UPLOAD_BATCH_SIZE;
                const end = Math.min(start + UPLOAD_BATCH_SIZE, sliceCount);
                const formData = new FormData();
                for (let i = start; i < end; i++) {
                    formData.append(
                        'files',
                        sliceBlobs[i],
                        `${seriesInstanceUID}_${i}.dcm`
                    );
                }
                await fetchSaveSliceForImageForPatient({
                    pathParams: {
                        patientID: patientId,
                        imageID: createdImageId,
                    },
                    body: formData as unknown as SaveSliceForImageForPatientRequestBody,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            enqueueSnackbar('Image upload complete.', { variant: 'success' });
        } catch (error) {
            handleError(
                error,
                `Failed to upload image ${seriesInstanceUID} to the ` +
                    `connector: ${getErrorMessage(error)}`
            );
            throw error;
        }
    };

    return { ensureCached };
}

async function findImageIdOnConnector(
    patientId: string,
    seriesInstanceUID: string
): Promise<string | undefined> {
    try {
        const patient = await fetchGetPatient({
            pathParams: { patientId },
        });
        return patient?.images?.find(
            (img) => img.seriesInstanceUID === seriesInstanceUID
        )?.id;
    } catch {
        // 404 / network error — treat as "not cached".
        return undefined;
    }
}
